import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { format, getDaysInMonth, startOfMonth } from 'date-fns'

import { toast } from '../../components/LsToast'
import { ScreenWrapper } from '../../components/ScreenWrapper'
import { getAvailabilityByMonth, setDayAvailability } from '../../service/instructor-availability.service'
import { getInstructors } from '../../service/instructors.service'
import { getLessonsByDate } from '../../service/lessons.service'
import { InstructorAvailabilityDto, InstructorDto, LessonDto } from '../../service/types'

/**
 * Return the set of hours occupied by lessons assigned to a given instructor on a given date.
 */
const getOccupiedHours = (lessons: LessonDto[], instructorId: string): Set<number> => {
  const occupied = new Set<number>()
  for (const lesson of lessons) {
    if (!lesson.instructorIds.includes(instructorId)) continue
    const startH = parseInt(lesson.startTime.split(':')[0], 10)
    const endH = parseInt(lesson.endTime.split(':')[0], 10)
    const endM = parseInt(lesson.endTime.split(':')[1], 10)
    const lastHour = endM > 0 ? endH : endH - 1
    for (let h = startH; h <= lastHour; h++) {
      occupied.add(h)
    }
  }
  return occupied
}

const HOURS = [8, 9, 10, 11, 12, 13, 14, 15]
const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

type DragState = {
  date: string
  startHour: number
  endHour: number
  mode: 'add' | 'remove'
}

export const InstructorAvailability: FC = () => {
  const { t } = useTranslation()
  const [instructors, setInstructors] = useState<InstructorDto[]>([])
  const [selectedInstructorId, setSelectedInstructorId] = useState<string>('')
  const [selectedMonth, setSelectedMonth] = useState<Date>(startOfMonth(new Date()))
  const [availability, setAvailability] = useState<InstructorAvailabilityDto[]>([])
  const [loading, setLoading] = useState(false)
  const [drag, setDrag] = useState<DragState | null>(null)
  const dragRef = useRef<DragState | null>(null)
  const availableSetRef = useRef<Set<string>>(new Set())

  // Load instructors on mount
  useEffect(() => {
    const load = async () => {
      try {
        const res = await getInstructors()
        setInstructors(res.data)
        if (res.data.length > 0 && !selectedInstructorId) {
          setSelectedInstructorId(res.data[0].id)
        }
      } catch {
        toast.error(t('general.notification.unexpectedError'))
      }
    }
    void load()
    // eslint-disable-next-line -- load instructors once on mount (exhaustive-deps)
  }, [])

  // Load availability when instructor or month changes
  const monthStr = format(selectedMonth, 'yyyy-MM')

  const loadAvailability = useCallback(async () => {
    if (!selectedInstructorId) return
    setLoading(true)
    try {
      const res = await getAvailabilityByMonth(selectedInstructorId, monthStr)
      setAvailability(res.data)
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    } finally {
      setLoading(false)
    }
  }, [selectedInstructorId, monthStr, t])

  useEffect(() => {
    void loadAvailability()
  }, [loadAvailability])

  // Build a Set for fast lookup: "YYYY-MM-DD|hour"
  const availableSet = useMemo(() => {
    const set = new Set<string>()
    availability.forEach((a) => set.add(`${a.date}|${a.hour}`))
    availableSetRef.current = set
    return set
  }, [availability])

  const isAvailable = (date: string, hour: number) => availableSet.has(`${date}|${hour}`)

  // Generate days for the selected month
  const days = useMemo(() => {
    const count = getDaysInMonth(selectedMonth)
    const result: { date: string; dayKey: string; dayNum: number }[] = []
    for (let d = 1; d <= count; d++) {
      const dateObj = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), d)
      const dateStr = format(dateObj, 'yyyy-MM-dd')
      result.push({
        date: dateStr,
        dayKey: DAY_KEYS[dateObj.getDay()],
        dayNum: d,
      })
    }
    return result
  }, [selectedMonth])

  // Drag helpers
  const getDragRange = (d: DragState): number[] => {
    const min = Math.min(d.startHour, d.endHour)
    const max = Math.max(d.startHour, d.endHour)
    return HOURS.filter((h) => h >= min && h <= max)
  }

  const isInDragRange = (date: string, hour: number): boolean => {
    if (!drag || drag.date !== date) return false
    const min = Math.min(drag.startHour, drag.endHour)
    const max = Math.max(drag.startHour, drag.endHour)
    return hour >= min && hour <= max
  }

  const getCellState = (date: string, hour: number): boolean => {
    if (drag && isInDragRange(date, hour)) {
      return drag.mode === 'add'
    }
    return isAvailable(date, hour)
  }

  const handlePointerDown = (date: string, hour: number) => {
    const mode = isAvailable(date, hour) ? 'remove' : 'add'
    const state: DragState = { date, startHour: hour, endHour: hour, mode }
    dragRef.current = state
    setDrag(state)
  }

  const handlePointerEnter = (date: string, hour: number) => {
    const d = dragRef.current
    if (!d || d.date !== date) return
    const updated = { ...d, endHour: hour }
    dragRef.current = updated
    setDrag(updated)
  }

  const handlePointerUp = async () => {
    const d = dragRef.current
    if (!d) return
    dragRef.current = null
    setDrag(null)

    const draggedHours = getDragRange(d)
    // Compute new hours for the day: apply the drag mode on top of existing state
    const existingHours = HOURS.filter((h) => availableSetRef.current.has(`${d.date}|${h}`))

    // When removing hours, check that no lessons occupy those slots
    if (d.mode === 'remove') {
      try {
        const res = await getLessonsByDate(d.date)
        const occupied = getOccupiedHours(res.data, selectedInstructorId)
        const blocked = draggedHours.filter((h) => occupied.has(h))
        if (blocked.length > 0) {
          toast.error(t('availability.hasLessons'))
          return
        }
      } catch {
        toast.error(t('general.notification.unexpectedError'))
        return
      }
    }

    let newHours: number[]
    if (d.mode === 'add') {
      newHours = [...new Set([...existingHours, ...draggedHours])].sort((a, b) => a - b)
    } else {
      const removeSet = new Set(draggedHours)
      newHours = existingHours.filter((h) => !removeSet.has(h))
    }

    // Optimistic update
    setAvailability((prev) => {
      const filtered = prev.filter((a) => a.date !== d.date)
      const added = newHours.map((h) => ({ id: '', instructorId: selectedInstructorId, date: d.date, hour: h }))
      return [...filtered, ...added]
    })

    try {
      const res = await setDayAvailability({ instructorId: selectedInstructorId, date: d.date, hours: newHours })
      setAvailability((prev) => [...prev.filter((a) => a.date !== d.date), ...res.data])
    } catch {
      toast.error(t('general.notification.unexpectedError'))
      void loadAvailability()
    }
  }

  // Toggle entire day
  const handleDayToggle = async (date: string) => {
    const dayHours = HOURS.filter((h) => isAvailable(date, h))
    const allAvailable = dayHours.length === HOURS.length
    const newHours = allAvailable ? [] : HOURS

    // When removing hours, check that no lessons occupy those slots
    if (allAvailable) {
      try {
        const res = await getLessonsByDate(date)
        const occupied = getOccupiedHours(res.data, selectedInstructorId)
        if (occupied.size > 0) {
          toast.error(t('availability.hasLessons'))
          return
        }
      } catch {
        toast.error(t('general.notification.unexpectedError'))
        return
      }
    }

    try {
      const res = await setDayAvailability({ instructorId: selectedInstructorId, date, hours: newHours })
      setAvailability((prev) => [...prev.filter((a) => a.date !== date), ...res.data])
    } catch {
      toast.error(t('general.notification.unexpectedError'))
    }
  }

  const filter = (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <FormControl sx={{ minWidth: 200 }} size="small">
        <InputLabel>{t('availability.selectInstructor')}</InputLabel>
        <Select
          value={selectedInstructorId}
          label={t('availability.selectInstructor')}
          onChange={(e) => setSelectedInstructorId(e.target.value)}
        >
          {instructors.map((i) => (
            <MenuItem key={i.id} value={i.id}>
              {i.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <DatePicker
        views={['month', 'year']}
        value={selectedMonth}
        onChange={(val) => val && setSelectedMonth(startOfMonth(val))}
        slotProps={{ textField: { size: 'small' } }}
      />
    </Box>
  )

  return (
    <ScreenWrapper title={t('availability.title')} filter={filter}>
      {!selectedInstructorId ? (
        <Box sx={{ p: 2 }}>{t('availability.noInstructors')}</Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 2,
            opacity: loading ? 0.5 : 1,
            userSelect: 'none',
            maxHeight: 'calc(100vh - 160px)',
            overflow: 'auto',
          }}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 48,
                    height: 55,
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {''}
                </TableCell>
                <TableCell
                  sx={{
                    width: 120,
                    height: 55,
                    fontWeight: 'bold',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    backgroundColor: 'background.paper',
                  }}
                >
                  {''}
                </TableCell>
                {HOURS.map((h) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      px: 0.5,
                      height: 55,
                      borderBottom: '2px solid',
                      borderColor: 'divider',
                      position: 'sticky',
                      top: 0,
                      zIndex: 1,
                      backgroundColor: 'background.paper',
                    }}
                  >
                    {`${h}:00`}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {days.map(({ date, dayKey, dayNum }) => {
                const dayAvailableCount = HOURS.filter((h) => isAvailable(date, h)).length
                const allAvailable = dayAvailableCount === HOURS.length
                const isWeekend = dayKey === 'sat' || dayKey === 'sun'
                return (
                  <TableRow key={date}>
                    <TableCell sx={{ p: 0 }}>
                      <IconButton sx={{ minWidth: 48, minHeight: 48 }} onClick={() => handleDayToggle(date)}>
                        {allAvailable ? <CheckBoxIcon color="success" /> : <CheckBoxOutlineBlankIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ p: 0.5, fontWeight: isWeekend ? 'bold' : 'normal' }}>
                      {`${t(`availability.days.${dayKey}`)} ${dayNum}`}
                    </TableCell>
                    {HOURS.map((h) => {
                      const active = getCellState(date, h)
                      return (
                        <TableCell
                          key={h}
                          align="center"
                          role="button"
                          tabIndex={0}
                          aria-label={`${date} ${h}:00 — ${active ? t('availability.available') : t('availability.unavailable')}`}
                          onPointerDown={(e) => {
                            e.preventDefault()
                            handlePointerDown(date, h)
                          }}
                          onPointerEnter={() => handlePointerEnter(date, h)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              handlePointerDown(date, h)
                              void handlePointerUp()
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            p: 0.5,
                            backgroundColor: active ? '#4CAF5030' : 'transparent',
                            border: '1px solid',
                            borderColor: 'divider',
                            '&:hover': {
                              backgroundColor: active ? '#4CAF5050' : 'action.hover',
                            },
                            minWidth: 48,
                            minHeight: 48,
                            height: 48,
                            touchAction: 'none',
                          }}
                        />
                      )
                    })}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </ScreenWrapper>
  )
}
